/* LEGAL-NOTICE
   © Copyright 2018 - Quid Informatica S.p.A. - All Rights Reserved
   https://quidinfo.it/qinetic/licence.html - "Quid Consumer Lending Platform"
   Deposito SIAE n. 012702 del 26 Luglio 2018
LEGAL-NOTICE */
import { HttpParams } from '@angular/common/http';
import { ApiService } from '@api/services/api/api.service';
import {
  PositionDTO,
  NoteDTO,
  NotificationDTO,
  ChannelDTO,
  TaskDTO,
  FinalizeDTO
} from '@api/models/';
import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, throwError, forkJoin, of } from 'rxjs';
import { map, filter, tap } from 'rxjs/operators';
import { Position } from '@models/position';
import { ERRORS } from '@core/models';
import { AppService } from '@services/app/app.service';

@Injectable({
  providedIn: 'root'
})
export class PositionService {
  private position$: BehaviorSubject<Position> = new BehaviorSubject<Position>(
    null
  );

  private channel$: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  constructor(private apiService: ApiService, private appService: AppService) {}

  /**
   * carica i dati della position richiesta
   */
  loadPosition(positionId: string): Observable<boolean> {
    if (!positionId) {
      return throwError('wrn.position_loading_error');
    }

    return forkJoin([
      this.apiService.getPosition({ pathParams: { positionId } }),
      this.apiService.getChannel({ pathParams: { positionId } })
    ]).pipe(
      map(res => {
        this.setPosition(res[0]);
        this.setChannel(res[1]);
      }),
      tap(() => this.checkRetreat()),
      tap(() => this.checkConcluded()),
      map(() => true)
    );
  }

  /**
   * ricarica la position
   */
  reloadPosition(): Observable<boolean> {
    return this.loadPosition(this.getPositionId());
  }

  /**
   * restituisce osservabile per dati position
   */
  getPositionWhenChange(
    options: { whenExists: boolean } = { whenExists: true }
  ): Observable<Position> {
    return this.position$
      .asObservable()
      .pipe(filter(x => !options.whenExists || x instanceof Position));
  }

  /**
   * restituisce la position attuale
   */
  getPosition(): Position {
    return this.position$.getValue();
  }

  /**
   * restituisce osservabile per il canale
   */
  getChannelWhenChange(): Observable<string> {
    return this.channel$.asObservable().pipe(filter(x => !!x));
  }

  /**
   * restituisce il valore attuale del canale
   */
  getChannel(): string {
    return this.channel$.getValue();
  }

  /**
   * stabilisce se il canale attuale è un canale speciale
   */
  isSpecialChannel(): boolean {
    const channel = this.getChannel();
    const specialChannels =
      this.appService.getConfig().position.specialChannels || [];

    return (
      typeof channel === 'string' &&
      specialChannels.includes(channel.toUpperCase())
    );
  }

  /**
   * restituisce il position id
   */
  getPositionId(): string {
    const position = this.getPosition();
    return position && position.data.positionId;
  }

  /**
   * verifica se è necessario eseguire ripresa di altra patrica
   */
  existsResumePosition(): Observable<boolean> {
    return this.apiService
      .getResumePositionInspect({
        pathParams: { positionId: this.getPositionId() }
      })
      .pipe(map(res => res && res.exists === true));
  }

  /**
   * ritira la pratica
   */
  withdrawPosition(reasonCode: string): Observable<any> {
    const positionId = this.getPositionId();

    return this.apiService.postPositionWithdraw({
      pathParams: { positionId },
      body: { reasonCode }
    });
  }

  /**
   * effettua finalizzazione pratica
   */
  finalizePosition(): Observable<FinalizeDTO> {
    const positionId = this.getPositionId();

    return this.apiService.postPositionFinalize({
      pathParams: { positionId }
    });
  }

  /**
   * effettua rilascio pratica
   */
  releasePosition(): Observable<any> {
    const positionId = this.getPositionId();

    return this.apiService.postPositionRelease({
      pathParams: { positionId }
    });
  }

  /**
   * ritorna la lista di note
   */
  getNotes(type: string): Observable<NoteDTO[]> {
    const positionId = this.getPositionId();

    return this.apiService.getNotes({ pathParams: { positionId, type } });
  }

  /**
   * ritorna la lista di notifiche
   */
  getNotifications(): Observable<NotificationDTO[]> {
    const positionId = this.getPositionId();

    return this.apiService
      .getNotifications({ pathParams: { positionId } })
      .pipe(map(res => res.filter(x => x.notificationGroup === 'WEBAPP')));
  }

  /**
   * chiude la notifica
   */
  closeNotification(id: string, update: NotificationDTO): Observable<void> {
    return this.apiService.putNotification({
      pathParams: { notificationId: id, outcome: '2' },
      body: update
    });
  }

  /**
   * controlla se sulla position è presente il task
   */
  doesPositionHasTask(taskCode: string): Observable<boolean> {
    const params = new HttpParams().set('locked', 'true');

    return this.apiService
      .getPersonalTasks({ options: { params } })
      .pipe(map(res => !!this.findTask(res.tasks, taskCode)));
  }

  /**
   * imposta la position
   */
  private setPosition(dto: PositionDTO): void {
    this.position$.next(new Position(dto));
  }

  /**
   * imposta il canale
   */
  private setChannel(dto: ChannelDTO): void {
    this.channel$.next((dto && dto.channel) || null);
  }

  /**
   * trova nella lista il task
   */
  private findTask(tasks: TaskDTO[], taskCode: string): TaskDTO {
    return tasks.find(
      x => x.businessId === this.getPositionId() && x.code === taskCode
    );
  }

  /**
   * controlla se la pratica è ritirata e genera errore
   */
  private checkRetreat(): void {
    if (
      this.appService.getConfig().position.preventNavOfRetreatPosition &&
      this.isRetreat()
    ) {
      throw new Error(ERRORS.retreatPosition);
    }
  }

  /**
   * controlla se la pratica è conclusa e genera errore
   */
  private checkConcluded(): void {
    if (
      this.appService.getConfig().position.preventNavOfConcludedPosition &&
      this.isConcluded()
    ) {
      throw new Error(
        ERRORS.concludedPosition + '_' + this.getPosition().getStatus()
      );
    }
  }

  /**
   * controlla se la pratica è ritirata e genera errore
   */
  private isRetreat(): boolean {
    const position = this.getPosition();

    return position && position.isRetreat();
  }

  /**
   * controlla se la pratica risulta conclusa
   */
  private isConcluded(): boolean {
    const position = this.getPosition();
    const considerConcludedWhen =
      this.appService.getConfig().position.considerPositionConcludedWhen;

    if (
      !position ||
      !considerConcludedWhen ||
      considerConcludedWhen.length === 0
    ) {
      return false;
    }

    return considerConcludedWhen.some(combination =>
      position.hasPhaseAndStatus(combination)
    );
  }
}
